// p-editor-helpers.js
(() => {
  const MPE = (window.MPE = window.MPE || {});

  // --- dom ---
  const $  = (sel, r=document) => r.querySelector(sel);
  const $$ = (sel, r=document) => Array.from(r.querySelectorAll(sel));
  const px = v => Number.parseFloat(v) || 0;
  const sideOf = (zone='') => String(zone).split('-')[0] || '';

  // --- text utils ---
  function handleize(s){return String(s).toLowerCase().replace(/&/g,'and').replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'');}
  function parseValue(val){
    const parts=String(val).split('_'); const gRaw=parts[0]||''; const zRaw=parts.slice(1).join('_')||'';
    const g=handleize(gRaw), z=handleize(zRaw); return {group:g,key:g+'-'+z,zone:z};
  }

  // --- storage ---
  const storage = {
    get(key, fallback=null){ try{ const v = localStorage.getItem(key); return v==null?fallback:JSON.parse(v); }catch{ return fallback; } },
    set(key, val){ try{ localStorage.setItem(key, JSON.stringify(val)); }catch{} },
    del(key){ try{ localStorage.removeItem(key); }catch{} }
  };

  // --- deps ---
  const deps = {
    hasCanvg(){ return !!(window.canvg && window.canvg.Canvg); },
    hasJsPDF(){ return !!(window.jspdf && window.jspdf.jsPDF); },
    requireCanvg(){ if (!this.hasCanvg()) throw new Error('canvg v3 missing'); return window.canvg.Canvg; },
    requireJsPDF(){ if (!this.hasJsPDF()) throw new Error('jsPDF missing'); return window.jspdf.jsPDF; },
    async waitForLibs(timeoutMs=8000){
      const start = performance.now();
      while (!(this.hasCanvg() && this.hasJsPDF())) {
        if (performance.now() - start > timeoutMs) break;
        await new Promise(r => setTimeout(r, 50));
      }
    }
  };

  // --- placeholders ---
  const PLACEHOLDERS = {
    initialen:  'INI',
    text:  'text',
    nummer:     '99',
    spielername:'SPIELERNAME',
    vereinsname:'VereinsNAME'
  };

  // --- zones ---
  const ZONES_CACHE = new Map();
  function getZoneRect(zoneId) {
    if (!zoneId) return null;
    if (ZONES_CACHE.has(zoneId)) return ZONES_CACHE.get(zoneId);
    const sym = document.querySelector(`.mpe-sprite symbol#${CSS.escape(zoneId)}`);
    if (!sym) return null;

    let r = sym.querySelector('rect.zone-outline') || sym.querySelector('rect');
    let x=0, y=0, w=0, h=0;
    if (r) { x = px(r.getAttribute('x')); y = px(r.getAttribute('y')); w = px(r.getAttribute('width')); h = px(r.getAttribute('height')); }
    else {
      const vb = (sym.getAttribute('viewBox')||'').trim().split(/\s+/).map(Number);
      if (vb.length !== 4) return null; x=vb[0]; y=vb[1]; w=vb[2]; h=vb[3];
    }
    const rect = { x, y, w, h, cx: x + w/2, cy: y + h/2 };
    ZONES_CACHE.set(zoneId, rect);
    return rect;
  }
  function measureZone(zoneId) {
    const r = getZoneRect(zoneId);
    if (!r) return null;
    const pad = 0.01;
    return { ...r, iw: r.w * (1 - pad*2), ih: r.h * (1 - pad*2) };
  }

  // --- text fit ---
  function splitToTwoLines(text) {
    if (!text) return [''];
    const parts = String(text).split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length <= 1) return [parts[0] || ''];
    return [parts[0], parts.slice(1).join(' ')];
  }
  function measureMultilineBBox(tempText, fontSize, lines, lineHeightEm) {
    while (tempText.firstChild) tempText.removeChild(tempText.firstChild);
    const t1 = document.createElementNS('http://www.w3.org/2000/svg','tspan');
    t1.setAttribute('x','0'); t1.setAttribute('dy','0'); t1.textContent = lines[0] || '';
    tempText.appendChild(t1);
    if (lines.length > 1) {
      const t2 = document.createElementNS('http://www.w3.org/2000/svg','tspan');
      t2.setAttribute('x','0'); t2.setAttribute('dy',`${lineHeightEm}em`); t2.textContent = lines[1] || '';
      tempText.appendChild(t2);
    }
    tempText.setAttribute('font-size', fontSize);
    return tempText.getBBox();
  }
  function fitTextToZone(textVal, zoneId, kind='text') {
    const M = measureZone(zoneId);
    if (!M) return null;
    const svgNS = 'http://www.w3.org/2000/svg';
    const tempSvg  = document.createElementNS(svgNS,'svg');
    const tempText = document.createElementNS(svgNS,'text');
    tempText.setAttribute('text-anchor','start');
    tempText.setAttribute('dominant-baseline','alphabetic');
    tempSvg.appendChild(tempText);
    tempSvg.style.position = 'absolute';
    tempSvg.style.left = '-99999px';
    document.body.appendChild(tempSvg);

    if (kind === 'number') {
      const sampleSize = 100;
      const bbox0 = measureMultilineBBox(tempText, sampleSize, [String(textVal||'')], 1.0);
      const factorW = (M.iw / bbox0.width)  * sampleSize;
      const factorH = (M.ih / bbox0.height) * sampleSize;
      const font = Math.max(5, Math.min(factorW * 0.98, factorH));
      tempSvg.remove();
      return { ...M, font, lines: [String(textVal||'')], lineHeightEm: 1.0 };
    }

    const lines = splitToTwoLines(textVal).slice(0, 2);
    const lineHeightEm = 0.5;
    let lo = 5, hi = Math.max(6, M.ih), best = lo;
    for (let it = 0; it < 22; it++) {
      const mid = (lo + hi) / 2;
      const bbox = measureMultilineBBox(tempText, mid, lines, lineHeightEm);
      const fits = bbox.height <= M.ih + 0.5 && bbox.width <= M.iw + 0.5;
      if (fits) { best = mid; lo = mid; } else hi = mid;
    }
    tempSvg.remove();
    return { ...M, font: best, lines, lineHeightEm };
  }

  // --- svg/io ---
  function absUrl(u) {
    try {
      if (!u) return '';
      if (/^data:/i.test(u)) return u;
      if (/^https?:/i.test(u)) return u;
      if (u.startsWith('//')) return window.location.protocol + u;
      const a = document.createElement('a'); a.href = u; return a.href;
    } catch { return u; }
  }
  async function toDataURL(url) {
    if (!url || /^data:/i.test(url)) return url;
    try {
      const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error('HTTP '+res.status);
      const blob = await res.blob();
      const data = await new Promise(resolve => {
        const fr = new FileReader(); fr.onload = () => resolve(fr.result); fr.readAsDataURL(blob);
      });
      return data;
    } catch { return url; }
  }
  async function inlineImagesToData(svgRoot, { strict = true, placeholder = true } = {}) {
    const images = $$('image', svgRoot);
    for (const img of images) {
      let href = img.getAttributeNS('http://www.w3.org/1999/xlink', 'href') || img.getAttribute('href') || '';
      if (!href) continue;
      // вже data: — ок
      if (/^data:/i.test(href)) continue;
      // blob: у межах сторінки — як правило ок і не «таїнтить» canvas
      if (/^blob:/i.test(href)) {
          try {
            const res = await fetch(href);
            const blb = await res.blob();
            const data = await new Promise((resolve) => {
              const fr = new FileReader();
              fr.onload = () => resolve(fr.result);
              fr.readAsDataURL(blb);
            });
            if (data && /^data:/i.test(data)) {
              img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', data);
              img.setAttribute('href', data);
              img.setAttribute('crossorigin', 'anonymous');
              continue;
            }
          } catch {
            // якщо не вдалось — впадемо далі на strict/placeholder логіку
          }
        }

      const full = absUrl(href);
      let data = null;
      try {
        data = await toDataURL(full);
      } catch {}

      if (data && /^data:/i.test(data)) {
        img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', data);
        img.setAttribute('href', data);
        img.setAttribute('crossorigin', 'anonymous');
        continue;
      }

      // Інлайнинг НЕ вдався (жорсткий CORS тощо)
      if (strict) {
        if (placeholder) {
          const x = img.getAttribute('x') || '0';
          const y = img.getAttribute('y') || '0';
          const w = img.getAttribute('width')  || '0';
          const h = img.getAttribute('height') || '0';
          const ph = img.ownerDocument.createElementNS(svgRoot.namespaceURI, 'rect');
          ph.setAttribute('x', x); ph.setAttribute('y', y);
          ph.setAttribute('width', w); ph.setAttribute('height', h);
          ph.setAttribute('fill', '#f1f5f9');
          ph.setAttribute('stroke', '#94a3b8'); ph.setAttribute('stroke-dasharray', '4 3');
          img.parentNode.replaceChild(ph, img);
        } else {
          img.remove();
        }
      } else {
        img.setAttribute('crossorigin', 'anonymous');
      }
    }
  }

  async function fetchSvgText(url) {
    const full = absUrl(url);
    try {
      const res = await fetch(full, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return await res.text();
    } catch { return null; }
  }
  function extractFragmentId(href) {
    if (!href) return null;
    const s = absUrl(href);
    const idx = s.indexOf('#');
    if (idx < 0) return null;
    return { url: s.slice(0, idx), id: s.slice(idx + 1) };
  }
  function cloneChildrenInto(targetParent, sourceNode) {
    const tmp = sourceNode.cloneNode(true);
    while (tmp.firstChild) targetParent.appendChild(tmp.firstChild);
  }
 async function inlineExternalUses(containerSvg) {
  const $$ = (sel, r=document) => Array.from(r.querySelectorAll(sel));

  // Збираємо зовнішні <use> (з http/https або //)
  const uses = $$('use', containerSvg).filter(u => {
    const href = u.getAttributeNS('http://www.w3.org/1999/xlink','href') || u.getAttribute('href') || '';
    if (!href) return false;
    const h = href.trim();
    return /^https?:\/\//i.test(h) || h.startsWith('//');
  });
  if (!uses.length) return;

  // Групуємо по URL файла
  const byUrl = new Map();
  function absUrl(u) {
    try {
      if (!u) return '';
      if (/^data:|^blob:|^https?:/i.test(u)) return u;
      if (u.startsWith('//')) return window.location.protocol + u;
      const a = document.createElement('a'); a.href = u; return a.href;
    } catch { return u; }
  }
  function extractFragmentId(href) {
    const s = absUrl(href || '');
    const i = s.indexOf('#');
    if (i < 0) return null;
    return { url: s.slice(0, i), id: s.slice(i + 1) };
  }
  for (const u of uses) {
    const href = u.getAttributeNS('http://www.w3.org/1999/xlink','href') || u.getAttribute('href');
    const info = extractFragmentId(href);
    if (!info) continue;
    (byUrl.get(info.url) || byUrl.set(info.url, []).get(info.url)).push({ use: u, id: info.id });
  }

  // Завантажуємо зовнішні SVG та інлайнемо
  async function fetchSvgText(url) {
    try {
      const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return await res.text();
    } catch { return null; }
  }

  for (const [url, list] of byUrl) {
    const txt = await fetchSvgText(url);
    if (!txt) continue;
    const parser = new DOMParser();
    const extDoc = parser.parseFromString(txt, 'image/svg+xml');

    for (const { use, id } of list) {
      const symbol = extDoc.querySelector(`symbol#${CSS.escape(id)}`);
      if (!symbol) continue;

      // 1) дані з <symbol viewBox>
      const vb = (symbol.getAttribute('viewBox') || '').trim().split(/\s+/).map(Number);
      const vbX = vb.length === 4 ? vb[0] : 0;
      const vbY = vb.length === 4 ? vb[1] : 0;
      const vbW = vb.length === 4 ? vb[2] : 0;
      const vbH = vb.length === 4 ? vb[3] : 0;

      // 2) дані з <use>
      const x = parseFloat(use.getAttribute('x') || '0') || 0;
      const y = parseFloat(use.getAttribute('y') || '0') || 0;
      // Якщо width/height не задані — беремо з viewBox (найчастіше так і є)
      let w = parseFloat(use.getAttribute('width')  || '') || 0;
      let h = parseFloat(use.getAttribute('height') || '') || 0;
      if ((!w || !h) && vbW && vbH) { w = vbW; h = vbH; }

      // 3) базове preserveAspectRatio: xMidYMid meet
      let sx = 1, sy = 1, tx = x, ty = y;
      if (vbW > 0 && vbH > 0 && w > 0 && h > 0) {
        const s = Math.min(w / vbW, h / vbH);
        sx = sy = s;

        // центруємо у рамці w×h
        const drawnW = vbW * s;
        const drawnH = vbH * s;
        const offX = (w - drawnW) / 2;
        const offY = (h - drawnH) / 2;

        // translate: у позицію (x+offX, y+offY), потім компенсуємо vbX/vbY
        tx = x + offX - vbX * s;
        ty = y + offY - vbY * s;
      } else {
        // Без viewBox/розмірів — просто компенсуємо vbX/vbY
        tx = x - vbX;
        ty = y - vbY;
      }

      // 4) Будуємо <g> з тим самим атрибутним набором, + правильний transform
      const NS = containerSvg.namespaceURI;
      const g = containerSvg.ownerDocument.createElementNS(NS, 'g');

      for (const { name, value } of Array.from(use.attributes)) {
        if (name === 'xlink:href' || name === 'href') continue;
        if (name === 'x' || name === 'y' || name === 'width' || name === 'height') continue; // тепер керуємо трансформом
        g.setAttribute(name, value);
      }
      g.setAttribute('transform', `translate(${tx},${ty}) scale(${sx},${sy})`);

      // 5) Копіюємо вміст symbol → g
      const tmp = symbol.cloneNode(true);
      while (tmp.firstChild) g.appendChild(tmp.firstChild);

      // 6) Замінюємо use на наш g
      use.parentNode.replaceChild(g, use);
    }
  }
}

  // --- fonts: embed @font-face into export SVG --------------------------------
  function _collectUsedFontFamilies(svgRoot) {
    const fams = new Set();
    const take = (v) => {
      if (!v) return;
      const raw = String(v).split(',')[0].trim().replace(/^['"]|['"]$/g, '');
      if (raw) fams.add(raw);
    };
    svgRoot.querySelectorAll('text,tspan').forEach(n => {
      take(n.getAttribute('font-family'));
      const st = n.getAttribute('style') || '';
      const m = st.match(/font-family\s*:\s*([^;]+)/i);
      if (m) take(m[1]);
    });
    return Array.from(fams);
  }

  function _ensureExplicitFontOnText(svgRoot, fallbackFamily='Montserrat') {
    const clean = (s) => String(s || '').split(',')[0].trim().replace(/^['"]|['"]$/g, '');
    svgRoot.querySelectorAll('text').forEach(t => {
      const attrFam = t.getAttribute('font-family');
      const st = t.getAttribute('style') || '';
      const m = st.match(/font-family\s*:\s*([^;]+)/i);
      const fam = clean(attrFam || (m && m[1]) || fallbackFamily);
      const finalFam = fam || 'Montserrat';
      t.setAttribute('font-family', `'${finalFam}'`);
      const ts = t.querySelector('tspan');
      if (ts && !/font-family\s*:/i.test(ts.getAttribute('style') || '')) {
        const prev = ts.getAttribute('style') || '';
        ts.setAttribute('style', `${prev}${prev && !prev.trim().endsWith(';') ? ';' : ''}font-family:'${finalFam}';`);
      }
    });
  }

  function _collectFontFaceCssForFamilies(families) {
    let css = '';
    for (const ss of Array.from(document.styleSheets)) {
      let rules;
      try { rules = ss.cssRules; } catch { continue; } // CORS — пропускаємо
      if (!rules) continue;
      for (const rule of Array.from(rules)) {
        if (rule.type === CSSRule.FONT_FACE_RULE) {
          const txt = rule.cssText || '';
          for (const fam of families) {
            const re = new RegExp(`font-family\\s*:\\s*['"]?${fam}['"]?`, 'i');
            if (re.test(txt)) { css += txt + '\n'; break; }
          }
        }
      }
    }
    return css;
  }

  async function _inlineFontFaceUrlsToData(css) {
    // інлайнемо url(...) → data:URL, використовуючи вже наявний toDataURL()
    const re = /url\(\s*(['"]?)([^'")]+)\1\s*\)/ig;
    const tasks = [];
    const urls = new Set();
    let m;
    while ((m = re.exec(css))) urls.add(m[2]);

    for (const u of urls) {
      try {
        const abs = absUrl(u);
        const data = await toDataURL(abs);
        if (data && data !== u) {
          // замінимо всі входження конкретного URL
          const esc = u.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const r2 = new RegExp(`url\\(\\s*(['"]?)${esc}\\1\\s*\\)`, 'g');
          css = css.replace(r2, `url('${data}')`);
        }
      } catch { /* ignore */ }
    }
    return css;
  }

  async function embedFontsIntoSvg(svgRoot, fallbackFamily='Montserrat') {
    _ensureExplicitFontOnText(svgRoot, fallbackFamily);
    const families = _collectUsedFontFamilies(svgRoot);
    if (!families.length) families.push(fallbackFamily);

    try { await document.fonts.ready; } catch {}
    await Promise.allSettled(families.map(f => {
      try { return document.fonts.load(`16px '${f}'`); } catch { return Promise.resolve(); }
    }));

    let css = _collectFontFaceCssForFamilies(families);
    if (css) css = await _inlineFontFaceUrlsToData(css);

    if (css && css.trim()) {
      let defs = svgRoot.querySelector('defs');
      if (!defs) {
        defs = svgRoot.ownerDocument.createElementNS(svgRoot.namespaceURI, 'defs');
        svgRoot.insertBefore(defs, svgRoot.firstChild);
      }
      const style = svgRoot.ownerDocument.createElementNS(svgRoot.namespaceURI, 'style');
      style.setAttribute('type', 'text/css');
      style.textContent = css;
      defs.appendChild(style);
    }
  }

  // --- preview/svg build ---
  function getPreviewHost(){ return document.querySelector('p-editor-preview'); }
  function getPreviewSvg(){ const host = getPreviewHost(); return host ? host.querySelector('.p-editor-preview__preview') : null; }
  function getBaseSize(svgEl){
    const vb = svgEl && svgEl.viewBox && svgEl.viewBox.baseVal
      ? { w: svgEl.viewBox.baseVal.width || 460, h: svgEl.viewBox.baseVal.height || 480 }
      : { w: 460, h: 480 };
    return vb;
  }
  async function ensureAllSidesRendered() {
    const host = getPreviewHost();
    if (!host) return;
    if (typeof host.renderAllSides === 'function') {
      host.renderAllSides();
      await new Promise(r => requestAnimationFrame(() => r()));
      return;
    }
    const labels = Array.from(host.querySelectorAll('[data-role="side-label"]'));
    const active = labels.find(el => el.getAttribute('aria-selected') === 'true') || null;
    for (const el of labels) {
      el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      await new Promise(r => requestAnimationFrame(() => r()));
    }
    if (active && active.isConnected) {
      active.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      await new Promise(r => requestAnimationFrame(() => r()));
    }
  }
  function buildCombinedSVG(previewSvg, {
    includeUnderlay = true,
    padOuter = 40,   // outer margin on all sides
    gutter = 40,     // spacing between tiles
    bgColor = '#fff' // page background
    } = {}) {
    const { w, h } = getBaseSize(previewSvg);
    const outW = padOuter + w + gutter + w + padOuter;
    const outH = padOuter + h + gutter + h + padOuter;

    const NS = 'http://www.w3.org/2000/svg';
    const out = document.createElementNS(NS, 'svg');
    out.setAttribute('xmlns', NS);
    out.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
    out.setAttribute('viewBox', `0 0 ${outW} ${outH}`);

    const defs = document.createElementNS(NS, 'defs');
    $$('defs, svg.mpe-sprite, .mpe-sprite', previewSvg).forEach(d => defs.appendChild(d.cloneNode(true)));
    $$('style', previewSvg).forEach(st => defs.appendChild(st.cloneNode(true)));
    out.appendChild(defs);

    const bg = document.createElementNS(NS, 'rect');
    bg.setAttribute('x','0'); bg.setAttribute('y','0');
    bg.setAttribute('width', String(outW)); bg.setAttribute('height', String(outH));
    bg.setAttribute('fill', bgColor);
    out.appendChild(bg);

    const underlayRoot = previewSvg.querySelector('.g-underlay');
    const contentSides = $$('.g-content .g-side', previewSvg);

    const pos = {
      front: { x: padOuter,          y: padOuter },
      back:  { x: padOuter + w + gutter, y: padOuter },
      left:  { x: padOuter,          y: padOuter + h + gutter },
      right: { x: padOuter + w + gutter, y: padOuter + h + gutter },
    };

    ['front','back','left','right'].forEach(side => {
      const tile = document.createElementNS(NS, 'g');
      tile.setAttribute('transform', `translate(${pos[side].x}, ${pos[side].y})`);

      if (includeUnderlay && underlayRoot) {
        const onlyIcon = underlayRoot.querySelector(`.icon[data-side="${side}"]`);
        if (onlyIcon) {
          const u = document.createElementNS(NS, 'g');
          for (const { name, value } of Array.from(underlayRoot.attributes)) u.setAttribute(name, value);
          u.classList.add('g-underlay');
          u.appendChild(onlyIcon.cloneNode(true));
          tile.appendChild(u);
        }
      }

      const sideEl = contentSides.find(g => g.getAttribute('data-side') === side);
      if (sideEl) {
        const sClone = sideEl.cloneNode(true);
        sClone.classList.remove('is-hidden');
        tile.appendChild(sClone);
      }
      out.appendChild(tile);
    });

    return { svg: out, width: outW, height: outH };
  }
// --- inline computed SVG styles (colors, strokes, fonts) ---
function _kebab(s){return String(s).replace(/[A-Z]/g, m=>'-'+m.toLowerCase());}

function inlineComputedStyles(svgRoot, props) {
  // Базовий набір для кольорів/обводок/тексту
  const base = [
    'fill','fillOpacity','stroke','strokeOpacity','opacity',
    'strokeWidth','strokeLinecap','strokeLinejoin','strokeMiterlimit',
    'strokeDasharray','strokeDashoffset',
    'color','stopColor','stopOpacity',
    'fontFamily','fontSize','fontWeight','fontStyle','textAnchor',
    'letterSpacing','wordSpacing','dominantBaseline'
  ];
  const wanted = (props && props.length) ? props : base;

  const all = svgRoot.querySelectorAll('*');
  all.forEach(el => {
    const cs = getComputedStyle(el);
    wanted.forEach(p => {
      const cssName = _kebab(p);
      let v = cs.getPropertyValue(cssName);
      if (!v) return;
      v = v.trim();
      // пропускаємо порожнє/дефолтне
      if (!v || v === 'initial') return;

      // невидиме — теж важливо зберегти
      // якщо 'none' для stroke/fill – записуємо як є
      // для fontFamily залишаємо перше сімейство
      if (p === 'fontFamily') {
        // беремо перше сімейство без лапок
        v = v.split(',')[0].trim().replace(/^['"]|['"]$/g,'');
        if (!v) return;
      }

      // мапимо імена атрибутів SVG (kebab-case)
      const attr = cssName;
      // не перезаписуємо, якщо вже є явний атрибут на елементі
      if (el.hasAttribute(attr)) return;

      // Записуємо атрибут
      el.setAttribute(attr, v);
    });
  });
}

async function prepareExportSvg(previewSvg) {
  ensureViewBoxAndSize(previewSvg);
  await ensureAllSidesRendered();

  const { svg, width, height } = buildCombinedSVG(previewSvg, { includeUnderlay: true });

  await inlineExternalUses(svg);
  await inlineImagesToData(svg, { strict: true, placeholder: true });

  try {
    const activeFont = document.querySelector('.pet-fonts__select')?.value || 'Montserrat';
    await embedFontsIntoSvg(svg, activeFont);
  } catch {}

  // ✅ НОВЕ: інлайнимо кольори/обводки/шрифти з обчислених стилів
  inlineComputedStyles(svg);

  // невелика пауза для стабілізації blob-url
  await new Promise(r => setTimeout(r, 200));

  const xml = new XMLSerializer().serializeToString(svg);
  return { svgString: xml, width, height };
}


function ensureViewBoxAndSize(svgEl, { w = 920, h = 960 } = {}) {
  if (!svgEl) return;
  const hasVB = !!svgEl.getAttribute('viewBox');
  if (!hasVB) svgEl.setAttribute('viewBox', `0 0 ${w} ${h}`);
  if (!svgEl.getAttribute('width'))  svgEl.setAttribute('width',  String(w));
  if (!svgEl.getAttribute('height')) svgEl.setAttribute('height', String(h));
}

  // --- expose ---
  Object.assign(MPE, {
    $, $$, px, sideOf,
    handleize, parseValue,
    storage,
    deps,
    PLACEHOLDERS,
    getZoneRect, measureZone,
    splitToTwoLines, measureMultilineBBox, fitTextToZone,
    svg: { absUrl, toDataURL, inlineImagesToData, fetchSvgText, extractFragmentId, cloneChildrenInto, inlineExternalUses, buildCombinedSVG },
    preview: { getPreviewHost, getPreviewSvg, getBaseSize, ensureAllSidesRendered, prepareExportSvg }
  });
})();
